import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export interface ReportData {
  title: string;
  subtitle?: string;
  date: string;
  headers: string[];
  rows: (string | number)[][];
  totals?: (string | number)[];
}

export function exportToCSV(data: ReportData, filename: string) {
  const csvRows = [
    [data.title],
    [data.subtitle || ""],
    [`Generated: ${data.date}`],
    [],
    data.headers,
    ...data.rows,
  ];
  if (data.totals) csvRows.push(data.totals.map((t) => String(t)));

  const csv = csvRows.map((row) => row.join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function exportToExcel(data: ReportData, filename: string) {
  const wb = XLSX.utils.book_new();
  const wsData = [
    [data.title],
    [data.subtitle || ""],
    [`Generated: ${data.date}`],
    [],
    data.headers,
    ...data.rows,
  ];
  if (data.totals) wsData.push(data.totals.map((t) => String(t)));

  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Set column widths
  const colWidths = data.headers.map((h, i) => ({
    wch: Math.max(h.length, ...data.rows.map((r) => String(r[i] || "").length)) + 2,
  }));
  ws["!cols"] = colWidths;

  XLSX.utils.book_append_sheet(wb, ws, "Report");
  XLSX.writeFile(wb, `${filename}.xlsx`);
}

export function exportToPDF(data: ReportData, filename: string) {
  const doc = new jsPDF();

  // Header
  doc.setFontSize(18);
  doc.setTextColor(26, 54, 93);
  doc.text(data.title, 14, 20);

  if (data.subtitle) {
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(data.subtitle, 14, 28);
  }

  doc.setFontSize(9);
  doc.setTextColor(150);
  doc.text(`Generated: ${data.date}`, 14, 35);

  // Table
  autoTable(doc, {
    startY: 42,
    head: [data.headers],
    body: data.rows.map((row) => row.map((cell) => String(cell))),
    foot: data.totals ? [data.totals.map((t) => String(t))] : undefined,
    theme: "striped",
    headStyles: {
      fillColor: [26, 54, 93],
      textColor: 255,
      fontStyle: "bold",
      fontSize: 9,
    },
    bodyStyles: {
      fontSize: 8,
    },
    footStyles: {
      fillColor: [240, 244, 248],
      textColor: [26, 54, 93],
      fontStyle: "bold",
      fontSize: 9,
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
    margin: { left: 14, right: 14 },
  });

  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 10, { align: "center" });
    doc.text("AccouPro | Computer Generated Report", 14, doc.internal.pageSize.height - 10);
  }

  doc.save(`${filename}.pdf`);
}

export function formatCurrency(amount: number): string {
  const sign = amount < 0 ? "-" : "";
  return sign + "₹" + Math.abs(amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
